import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Cog,
  GitBranch,
  LayoutDashboard,
  Plug,
  RefreshCw,
  Shield,
  ShieldCheck,
  Ticket,
  TrendingUp,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'why-digian', label: 'Why digian.ai' },
];

const AIWorkflowAutomationPage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const overviewIT = [
    'Password resets, account unlocks, MFA setup',
    'User provisioning, role changes, offboarding',
    'Software installs, license assignments',
    'Hardware dispatch, asset tracking',
    'VPN/network access configuration',
  ];

  const overviewCare = [
    'Order status, shipping updates, cancellations',
    'Billing adjustments, refunds, credits',
    'Appointment scheduling, reschedules',
    'Case creation, routing, escalation',
    'Account updates, subscription changes',
  ];

  const problems = [
    {
      title: 'AI Agents Can\'t Execute',
      desc: 'Most AI platforms only handle conversation. They cannot reset passwords, provision accounts, or update billing systems without heavy custom integration work.',
    },
    {
      title: 'Integration Hell',
      desc: 'Connecting AI to ITSM, CRM, identity providers, ERP, and custom apps requires months of API development and ongoing maintenance.',
    },
    {
      title: 'No Governance or Audit',
      desc: 'Without policy enforcement, approval workflows, and audit logging, enterprises can\'t trust AI to execute high-impact actions.',
    },
    {
      title: 'Tickets Still Require Human Touch',
      desc: 'Even when AI resolves an issue, agents still have to manually update ServiceNow, Zendesk, or Salesforce. That adds friction, time, and cost.',
    },
  ];

  const workflowSteps = [
    { num: '1', title: 'Trigger from AI Agent', desc: 'Voice or chat agent identifies user intent (e.g., "reset my password") and sends a workflow trigger to digian.ai via API.' },
    { num: '2', title: 'Data Capture & Context Enrichment', desc: 'System collects required parameters (user ID, device info, department) from the conversation and enterprise directories.' },
    { num: '3', title: 'Policy & Permission Validation', desc: 'Workflow engine checks RBAC rules, approval requirements, and business policies before executing any action.' },
    { num: '4', title: 'Backend System Execution', desc: 'Connects to target systems (Active Directory, ServiceNow, Salesforce, SAP, etc.) via 50+ pre-built connectors and executes the workflow.' },
    { num: '5', title: 'Ticket Creation or Update', desc: 'Automatically creates or updates tickets in ITSM/CRM with full context, resolution notes, and workflow metadata.' },
    { num: '6', title: 'User & Stakeholder Notifications', desc: 'Sends confirmation to the user via AI agent, email, or SMS. Managers, security teams, and approvers get notified automatically when needed.' },
    { num: '7', title: 'Audit Logging & Compliance', desc: 'Every step is logged with a timestamp, user identity, approval chain, and outcome. Fully ready for SOC 2, GDPR, or internal audit reviews.' },
  ];

  const capabilities = [
    {
      icon: Workflow,
      title: 'Workflow Orchestration',
      items: ['Visual workflow builder with no coding required', 'Conditional logic, branching and loops', 'Error handling and retry mechanisms', 'Multi-step orchestration across systems'],
      gradient: 'from-violet-500 to-purple-600',
    },
    {
      icon: Plug,
      title: 'Enterprise Integrations',
      items: ['50+ ready-made connectors for ITSM, CRM, identity and ERP', 'REST API support for custom and proprietary apps', 'Live and batch data sync', 'Secure credential management with OAuth and API keys'],
      gradient: 'from-purple-500 to-indigo-600',
    },
    {
      icon: ShieldCheck,
      title: 'Policy & Governance',
      items: ['Role-based access control (RBAC)', 'Approval workflows (manager, security, finance)', 'Business rule enforcement (spend limits, SLAs)', 'Compliance-ready audit trails'],
      gradient: 'from-indigo-500 to-blue-600',
    },
    {
      icon: Ticket,
      title: 'Ticket Lifecycle Management',
      items: ['Automatically create, update, or close tickets', 'Two-way sync with your ITSM and CRM', 'Contextual notes and resolution logging', 'SLA tracking and escalation triggers'],
      gradient: 'from-blue-500 to-cyan-600',
    },
    {
      icon: Bot,
      title: 'AI Augmentation',
      items: ['Intent recognition and entity extraction', 'Smart routing and escalation logic', 'Predictive workflow recommendations', 'Continuous learning from outcomes'],
      gradient: 'from-cyan-500 to-teal-600',
    },
  ];

  const whyItems = [
    { icon: Plug, title: '50+ Ready-Made Connectors', desc: 'No custom API development needed. Connect to ITSM, CRM, identity, ERP and more straight out of the box.' },
    { icon: Shield, title: 'Enterprise-Grade Security', desc: 'RBAC, SOC 2, GDPR-ready audit logs, encryption at rest and in transit.' },
    { icon: Zap, title: 'Fast Time to Value', desc: 'First workflows go live in weeks, not quarters. From pilot to full production in 4 to 8 weeks.' },
    { icon: LayoutDashboard, title: 'Visual Workflow Builder', desc: 'Drag and drop your way to production. No coding required for 90% of use cases.' },
    { icon: RefreshCw, title: 'Native ITSM/CRM Integration', desc: 'Bi-directional sync with ServiceNow, Zendesk, Salesforce, Jira Service Desk, etc.' },
    { icon: GitBranch, title: 'Policy and Approval Engine', desc: 'Enforce spend limits, manager approvals, and compliance rules. Fully configurable to your business policies.' },
    { icon: TrendingUp, title: 'Live Analytics', desc: 'Track deflection rates, AHT, CSAT and cost savings. Executive dashboards are included out of the box.' },
    { icon: Bot, title: 'Built for AI Agents', desc: 'Designed from the ground up for AI agents, not retrofitted from legacy RPA or iPaaS tools.' },
  ];

  const roadmapPhases = [
    {
      icon: Zap,
      title: 'Phase 1: Quick Wins (4–6 weeks)',
      items: ['Identify top 3–5 high-volume workflows', 'Configure pre-built connectors (ITSM, identity, email)', 'Build workflows using visual designer', 'Pilot with limited user group', 'Measure deflection, AHT, CSAT'],
    },
    {
      icon: TrendingUp,
      title: 'Phase 2: Scale (6–8 weeks)',
      items: ['Add 10–15 more workflows (billing, provisioning, scheduling)', 'Integrate CRM, ERP, custom apps', 'Implement approval chains and RBAC policies', 'Roll out to full user base', 'Establish KPI dashboards and reporting'],
    },
    {
      icon: Cog,
      title: 'Phase 3: Optimize (Ongoing)',
      items: ['Analyze workflow performance and user feedback', 'Refine logic, add new branches, reduce friction', 'Introduce AI-powered routing and predictions', 'Expand to new departments or geographies', 'Continuous ROI tracking and executive reporting'],
    },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="aiWorkflowAutomation" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian AI Workflow Automation","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Automate repetitive business processes end-to-end with Digian's AI workflow builder. No-code setup, enterprise-grade reliability.","url":"https://digian.ai/ai-workflow-automation","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"How long does it take to go live with first workflows?","acceptedAnswer":{"@type":"Answer","text":"Most customers have their first 3–5 high-volume workflows live within 4–6 weeks. Pre-built connectors and a visual workflow designer eliminate most of the setup time."}},{"@type":"Question","name":"Does this replace our existing ITSM or CRM?","acceptedAnswer":{"@type":"Answer","text":"No. digian.ai Workflow Automation integrates with your existing ITSM platforms like ServiceNow, Jira, and Zendesk, as well as CRM tools like Salesforce and HubSpot, using two-way sync. It orchestrates actions within those systems rather than replacing them."}},{"@type":"Question","name":"How do approval workflows work?","acceptedAnswer":{"@type":"Answer","text":"Approval chains are fully configurable by action type, spend threshold, or department. Approvals can be routed via email, Slack, Microsoft Teams, or directly inside the AI chat interface."}},{"@type":"Question","name":"What connectors are available out of the box?","acceptedAnswer":{"@type":"Answer","text":"Over 50 ready-made connectors covering Active Directory, Azure AD, Okta, ServiceNow, Salesforce, Zendesk, SAP, Workday, Jira, Slack, Microsoft 365 and more. Custom REST API support is available for proprietary systems."}}]}]}} />
      {/* Sticky Timeline Sidebar (desktop only) */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-violet-600 border-violet-600 scale-125'
                      : isPast
                      ? 'bg-violet-400 border-violet-400'
                      : 'bg-white border-slate-300 group-hover:border-violet-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-violet-600 font-semibold'
                      : isPast
                      ? 'text-violet-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Opening Section */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-indigo-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              AI Workflow Automation for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">
                Enterprise Support Operations
              </span>
            </h1>
            <p className="text-xl text-violet-600 font-medium">
              The execution engine behind AI-powered IT helpdesk and customer care
            </p>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-violet-50 border-l-4 border-violet-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-violet-600">digian.ai Workflow Automation</strong> turns AI conversations into real outcomes. Password resets, provisioning, billing updates, and hundreds more workflows run automatically from end to end with full governance, audit trails, and seamless ITSM integration.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
                      <Cog className="w-5 h-5 text-violet-600" /> IT Helpdesk Automation
                    </h3>
                    <div className="space-y-2">
                      {overviewIT.map((item, i) => (
                        <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.04 }}
                          className="flex items-start gap-2 p-2.5 bg-white rounded-lg hover:translate-x-1 transition-transform hover:shadow-sm">
                          <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-indigo-600" /> Customer Care Automation
                    </h3>
                    <div className="space-y-2">
                      {overviewCare.map((item, i) => (
                        <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.04 }}
                          className="flex items-start gap-2 p-2.5 bg-white rounded-lg hover:translate-x-1 transition-transform hover:shadow-sm">
                          <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-violet-200 mb-2">GOAL</h4>
                    <p className="text-lg">Close the gap between AI understanding and real backend execution</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-violet-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">End-to-end automation with governance, audit, and zero human touch</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Bridging the <span className="text-violet-600">Execution Gap</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              AI voice and chat agents excel at understanding intent, but most cannot execute backend workflows safely at scale with the governance enterprises require.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6">
                <h4 className="font-bold text-red-700 mb-2">{p.title}</h4>
                <p className="text-slate-700 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-8 bg-violet-50 border border-violet-200 rounded-xl p-5 text-center">
            <p className="text-violet-700 font-semibold">
              digian.ai Workflow Automation solves all four. It provides ready-made connectors, policy controls, audit trails, and full ticket lifecycle management in a single platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Core <span className="text-violet-600">Capabilities</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">The enterprise-grade features that make workflow automation production-ready</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${cap.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mb-3" />
                  <h4 className="text-lg font-bold mb-3">{cap.title}</h4>
                  <ul className="space-y-1.5">
                    {cap.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/90">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-white/70" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              How It <span className="text-violet-600">Works</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">From AI intent to completed task. Fully automated, governed, and auditable at every step.</p>
          </motion.div>

          <div className="flex flex-col gap-4">
            {workflowSteps.map((step, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 p-6 hover:border-violet-400 transition-colors">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white font-bold text-lg flex items-center justify-center flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section id="metrics" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Measurable <span className="text-violet-600">Impact</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Real-world benchmarks from enterprise deployments</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* IT Helpdesk */}
            <motion.div {...fadeUp} className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <Cog className="w-7 h-7" /> IT Helpdesk Outcomes
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="text-4xl font-extrabold">40–50%</div>
                  <div className="text-violet-200 mt-1">Ticket deflection rate. L1 tickets resolved with zero human involvement.</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">30–50%</div>
                  <div className="text-violet-200 mt-1">L1 workload reduction, freeing agents to focus on complex issues.</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">$25–40 → &lt;$5</div>
                  <div className="text-violet-200 mt-1">Cost per ticket. A dramatic reduction in overall support costs.</div>
                </div>
              </div>
            </motion.div>

            {/* Customer Care */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <Users className="w-7 h-7" /> Customer Care Outcomes
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-4xl font-extrabold">68%</div>
                  <div className="text-indigo-200 mt-1">Deflection rate. Customer inquiries resolved without a live agent.</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">60%</div>
                  <div className="text-indigo-200 mt-1">Average handle time reduction across all interactions.</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">85%</div>
                  <div className="text-indigo-200 mt-1">First contact resolution rate. Issues closed in a single interaction.</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">70%</div>
                  <div className="text-indigo-200 mt-1">Cost reduction in operational support expenses.</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Top <span className="text-violet-600">Use Cases</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">The most automated workflows across IT helpdesk and customer care</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* IT Helpdesk */}
            <motion.div {...fadeUp} className="bg-white rounded-xl border-t-4 border-violet-500 p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-5">
                <Cog className="w-6 h-6 text-violet-600" /> IT Helpdesk
              </h3>
              <div className="space-y-4">
                {[
                  { title: 'Password Resets & Account Unlocks', desc: 'Verify identity, reset AD and SSO credentials, and unlock accounts without any agent involvement.' },
                  { title: 'User Provisioning & Role Changes', desc: 'Onboard new hires, grant system access, update permissions based on role changes.' },
                  { title: 'Employee Onboarding & Offboarding', desc: 'Automate account creation, access provisioning and equipment assignment. Full deprovisioning handled automatically on exit.' },
                  { title: 'Software Installs & License Assignments', desc: 'Deploy software remotely, assign licenses and track usage. All steps are governed by your existing policies.' },
                  { title: 'Hardware Dispatch & Asset Tracking', desc: 'Approve requests, trigger shipping, log asset movements, update CMDB.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <ArrowRight className="w-4 h-4 text-violet-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{item.title}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Customer Care */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-white rounded-xl border-t-4 border-indigo-500 p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-5">
                <Users className="w-6 h-6 text-indigo-600" /> Customer Care
              </h3>
              <div className="space-y-4">
                {[
                  { title: 'Order Status & Shipping Updates', desc: 'Fetch real-time order info, initiate cancellations, trigger refunds or exchanges.' },
                  { title: 'Billing Adjustments & Refunds', desc: 'Process credits, apply discounts and issue refunds. High-value transactions automatically route for manager approval.' },
                  { title: 'Appointment Scheduling', desc: 'Check availability, book or cancel appointments, and send confirmations. Fully integrated with your calendar systems.' },
                  { title: 'Case Creation, Routing & Escalation', desc: 'Automatically create support cases in your CRM, route by priority and SLA, and escalate to the right specialist.' },
                  { title: 'Product Troubleshooting & Returns', desc: 'Guide customers through diagnostics, initiate return requests and process exchanges. All activity is tracked in your CRM.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <ArrowRight className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{item.title}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Implementation <span className="text-violet-600">Roadmap</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Go from pilot to production in weeks, not months.</p>
          </motion.div>

          <div className="flex flex-col gap-6">
            {roadmapPhases.map((phase, i) => {
              const Icon = phase.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border-l-8 border-violet-500 p-8 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="font-bold text-slate-900 text-lg flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-violet-600" /> {phase.title}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {phase.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why digian.ai Section */}
      <section id="why-digian" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Why <span className="text-violet-600">digian.ai</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">8 reasons enterprises choose us over build-your-own or legacy RPA</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-xl border-t-4 border-violet-400 p-6 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-violet-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 text-sm">{item.title}</h4>
                  <p className="text-slate-600 text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'How long does it take to go live with first workflows?', a: 'Most customers have their first 3–5 high-volume workflows live within 4–6 weeks. Pre-built connectors and a visual workflow designer eliminate most of the setup time.' },
              { q: 'Does this replace our existing ITSM or CRM?', a: 'No. digian.ai Workflow Automation integrates with your existing ITSM platforms like ServiceNow, Jira, and Zendesk, as well as CRM tools like Salesforce and HubSpot, using two-way sync. It orchestrates actions within those systems rather than replacing them.' },
              { q: 'How do approval workflows work?', a: 'Approval chains are fully configurable by action type, spend threshold, or department. Approvals can be routed via email, Slack, Microsoft Teams, or directly inside the AI chat interface.' },
              { q: 'What connectors are available out of the box?', a: 'Over 50 ready-made connectors covering Active Directory, Azure AD, Okta, ServiceNow, Salesforce, Zendesk, SAP, Workday, Jira, Slack, Microsoft 365 and more. Custom REST API support is available for proprietary systems.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-gradient-to-r from-violet-600 to-indigo-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Ready to Turn AI Conversations into <span className="text-violet-200">Real Outcomes?</span>
            </h2>
            <p className="text-xl text-violet-100">
              Let's identify your top 5 workflows for automation. We'll show you how to deploy them in weeks, not months.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Request a Workflow Assessment
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-violet-600">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIWorkflowAutomationPage;
